import React, { useState } from 'react';
import './CommunityGuideline.css';
import { AppHeader, BottomNavigation, CreateOptionsMenu, BackButton } from './shared';
import { useLogout, useUserData, useScrollToTop } from '../hooks';
import { useNavigation } from '../contexts/NavigationContext';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Issue #149: Community Guideline Page - Authenticated version
 * For logged-in users accessing from Profile page
 * Note: Community Guideline is only available in German as per requirements
 */
export function AuthenticatedCommunityGuideline() {
  const navigation = useNavigation();
  const { userData } = useUserData();
  const { handleLogout, isLoggingOut, logoutError, clearLogoutError } = useLogout(navigation.onLogout);
  const { t } = useLanguage();
  const [showCreateOptions, setShowCreateOptions] = useState(false);

  return (
    <CommunityGuidelineContent
      t={t}
      isAuthenticated={true}
      onNavigateBack={navigation.onNavigateBack}
      userData={userData}
      handleLogout={handleLogout}
      isLoggingOut={isLoggingOut}
      logoutError={logoutError}
      clearLogoutError={clearLogoutError}
      showCreateOptions={showCreateOptions}
      setShowCreateOptions={setShowCreateOptions}
    />
  );
}

/**
 * Shared content component for Community Guideline
 */
function CommunityGuidelineContent({
  t,
  isAuthenticated,
  onNavigateBack,
  userData,
  handleLogout,
  isLoggingOut,
  logoutError,
  clearLogoutError,
  showCreateOptions,
  setShowCreateOptions
}) {
  // Issue #374: Always start the document from the top, regardless of the
  // scroll position of the previously-displayed screen.
  useScrollToTop();

  // Community Guideline content - German only as per requirements
  // Note: This document is only available in German / Dieses Dokument ist nur auf Deutsch verfügbar
  const guidelineContent = `UNIVERSE COMMUNITY GUIDELINE
Gemeinschaftsrichtlinien

Stand: 11. Juli 2026

================================================================================
HINWEIS ZUR SPRACHE
================================================================================

Diese Community Guideline ist ausschließlich in deutscher Sprache verfügbar.
Als Plattform für Studierende an deutschen Hochschulen haben wir uns
entschieden, unsere Gemeinschaftsrichtlinien nur auf Deutsch anzubieten.

This Community Guideline is only available in German. As a platform for
students at German universities, we have decided to provide our community
guidelines in German only.

================================================================================
WILLKOMMEN BEI UNIVERSE
================================================================================

Hallo und herzlich willkommen in unserer Community!

UNIVERSE ist mehr als nur eine App - wir sind eine Gemeinschaft von
Studierenden an deutschen Hochschulen. Hier kannst du dich mit anderen
Studis vernetzen, im Marktplatz Sachen kaufen und verkaufen, Kurse
bewerten und dich über das Unileben austauschen.

Damit UNIVERSE ein positiver, sicherer und unterstützender Ort für alle
bleibt, haben wir diese Gemeinschaftsrichtlinien erstellt. Sie gelten für
alle Inhalte und Interaktionen auf unserer Plattform - egal ob Beiträge,
Kommentare, Marktplatz-Anzeigen, Kursbewertungen oder Chatnachrichten.

Bitte lies dir diese Richtlinien sorgfältig durch. Mit der Nutzung von
UNIVERSE erklärst du dich damit einverstanden, diese Regeln einzuhalten.

================================================================================
1. UNSERE GRUNDWERTE
================================================================================

1.1 Respekt und Wertschätzung

Behandle andere so, wie du selbst behandelt werden möchtest. Hinter jedem
Account steckt ein echter Mensch - ein Mitstudi, der genauso wie du sein
Studium meistern will.

1.2 Ehrlichkeit und Authentizität

Sei authentisch. Steh zu deinen Aussagen und verbreite keine Falsch-
informationen. Vertrauen ist die Basis unserer Community.

1.3 Inklusivität und Vielfalt

UNIVERSE ist für alle Studierenden da, unabhängig von Herkunft,
Geschlecht, sexueller Orientierung, Religion, Behinderung oder
politischer Überzeugung. Diskriminierung hat bei uns keinen Platz.

1.4 Akademische Integrität

Als Studierende teilen wir gemeinsame Werte. Wir unterstützen uns
gegenseitig beim Lernen, aber wir fördern kein Schummeln oder
Plagiate.

1.5 Sicherheit

Deine Sicherheit liegt uns am Herzen. Wir arbeiten aktiv daran, UNIVERSE
zu einem sicheren Ort zu machen, aber auch du trägst dazu bei.

================================================================================
2. RESPEKTVOLLER UMGANG MITEINANDER
================================================================================

2.1 Keine Belästigung und kein Mobbing

Was wir nicht tolerieren:
- Wiederholte negative oder einschüchternde Nachrichten an eine Person
- Öffentliches Bloßstellen oder Demütigungen
- Koordinierte Angriffe auf einzelne Personen
- Drohungen jeglicher Art
- Stalking oder obsessives Verfolgen

Tipp: Wenn jemand nicht auf deine Nachrichten reagiert oder dich blockiert
hat, respektiere das. Mehrfache Kontaktversuche nach einer Ablehnung
gelten als Belästigung.

2.2 Keine Hassrede und Diskriminierung

Absolut verboten sind:
- Beleidigungen oder Herabsetzungen aufgrund von Rasse, Ethnie, Nationalität,
  Religion, Geschlecht, sexueller Orientierung, Behinderung oder Alter
- Volksverhetzung und Aufstachelung zum Hass
- Holocaust-Leugnung oder Relativierung
- Verwendung verfassungswidriger Symbole (Hakenkreuze, SS-Runen, etc.)
- Verherrlichung von Nationalsozialismus oder anderen extremistischen Ideologien

Hinweis: Diese Inhalte sind in Deutschland strafbar (StGB §§ 86, 86a, 130).
Wir sind gesetzlich verpflichtet, solche Inhalte zu entfernen und ggf. an
Behörden zu melden.

2.3 Respektiere die Privatsphäre anderer

Veröffentliche keine:
- Privaten Daten anderer Personen (Adresse, Telefonnummer, etc.) ohne Erlaubnis
- Fotos oder Videos, auf denen andere erkennbar sind, ohne deren Zustimmung
- Private Nachrichten oder Gespräche ohne Einverständnis
- Intime oder kompromittierende Aufnahmen (auch wenn du sie freiwillig
  erhalten hast)

2.4 Keine Gewalt und Drohungen

Wir verbieten strikt:
- Androhung von körperlicher Gewalt
- Aufforderung zu Gewalt gegen Personen oder Gruppen
- Verherrlichung von Gewalt, Terrorismus oder gewalttätigen Ideologien
- Detaillierte Darstellung von realer Gewalt oder Folter
- Doxxing (Veröffentlichung persönlicher Daten mit schädlicher Absicht)

Wichtig: Drohungen werden ernst genommen. Bei konkreten Gewaltandrohungen
informieren wir die Polizei.

2.5 Keine sexuelle Belästigung

Verboten sind:
- Unaufgeforderte sexuelle Nachrichten oder Bilder
- Sexuelle Kommentare oder Anfragen, die nicht erwünscht sind
- Verbreitung intimer Aufnahmen ohne Zustimmung ("Revenge Porn")
- Jegliche Form von sexueller Nötigung oder Erpressung

================================================================================
3. VERBOTENE INHALTE
================================================================================

3.1 Illegale Inhalte

Folgende Inhalte sind auf UNIVERSE strengstens verboten:

a) Kindesmissbrauchsdarstellungen (CSAM)
   - Jegliche sexuelle Darstellung von Minderjährigen
   - Wir sind gesetzlich verpflichtet, solche Inhalte sofort dem
     Bundeskriminalamt (BKA) zu melden
   - Null-Toleranz-Politik: Sofortige Kontosperrung und Anzeige

b) Drogen und kontrollierte Substanzen
   - Verkauf, Kauf oder Tausch von illegalen Drogen
   - Anleitung zur Drogenherstellung
   - Werbung für Drogenkonsum
   - Verkauf von verschreibungspflichtigen Medikamenten ohne Rezept

c) Waffen und gefährliche Gegenstände
   - Verkauf von Schusswaffen, Messern (über 12 cm Klingenlänge),
     Schlagringen, Elektroschockern
   - Anleitungen zum Bau von Waffen oder Sprengstoffen
   - Feuerwerk und pyrotechnische Artikel

d) Betrug und illegale Aktivitäten
   - Betrugsversuche jeglicher Art
   - Identitätsdiebstahl
   - Phishing oder Verbreitung von Malware
   - Geldwäsche oder Hehlerei
   - Gefälschte Dokumente (Studentenausweise, Zeugnisse, etc.)

3.2 Sexuelle Inhalte

Nicht erlaubt sind:
- Pornografische Inhalte
- Explizit sexuelle Darstellungen
- Inhalte, die sexuelle Dienstleistungen anbieten
- Nacktheit in sexualisiertem Kontext

Ausnahme: Künstlerische oder edukative Darstellungen können im Einzelfall
zulässig sein, müssen aber mit einer Inhaltswarnung versehen werden.

3.3 Gewaltverherrlichende Inhalte

Verboten:
- Detaillierte Darstellungen von Gewalt gegen Menschen oder Tiere
- Verherrlichung von Terrorismus oder terroristischen Organisationen
- Gewaltaufrufe
- Inhalte, die Selbstverletzung oder Suizid fördern

Hinweis: Wenn du dich in einer Krise befindest, wende dich bitte an die
Telefonseelsorge: 0800 111 0 111 oder 0800 111 0 222 (kostenlos, 24/7)

3.4 Spam und Manipulation

Wir tolerieren nicht:
- Massenhaftes Posten identischer oder ähnlicher Inhalte
- Fake-Accounts oder Bots
- Künstliche Aufblähung von Likes oder Kommentaren
- Irreführende Links oder Clickbait
- Kommerzielle Werbung ohne Kennzeichnung

3.5 Falschinformationen

Verboten sind:
- Offensichtlich falsche Gesundheitsinformationen, die Schaden anrichten können
- Fälschung von Nachrichten oder offiziellen Mitteilungen
- Ausgeben als Institution, Behörde oder bekannte Persönlichkeit
- Erfundene Notfälle oder Katastrophenmeldungen

================================================================================
4. MARKTPLATZ-REGELN
================================================================================

4.1 Was du verkaufen darfst

Grundsätzlich erlaubt:
- Gebrauchte Bücher und Lernmaterialien
- Kleidung und Accessoires
- Elektronik (funktionstüchtig)
- Möbel und Einrichtung
- Sport- und Freizeitartikel
- Selbstgemachtes und Handarbeit
- Tickets für Veranstaltungen (zum Originalpreis)

4.2 Verbotene Artikel

Auf UNIVERSE darfst du NICHT verkaufen:
- Waffen aller Art (inkl. Messer über 12cm, Softair-Waffen)
- Drogen und Medikamente
- Alkohol und Tabak
- Nazi-Memorabilien oder verfassungswidrige Symbole
- Gefälschte Markenprodukte
- Gestohlene Waren
- Gefährliche Chemikalien oder Feuerwerkskörper
- Tiere (lebend oder tot)
- Pornografisches Material
- Akademische Arbeiten (Hausarbeiten, Abschlussarbeiten zum Verkauf)
- Gefälschte Dokumente oder Ausweise

4.3 Anzeigen-Anforderungen

Jede Anzeige muss enthalten:
- Eine ehrliche Beschreibung des Artikels (mind. 20 Zeichen)
- Mindestens ein echtes Foto des tatsächlichen Artikels
  (keine Stockfotos oder Bilder aus dem Internet!)
- Einen Preis oder die Angabe "Verhandlungsbasis"
- Den Zustand des Artikels (Neu / Wie neu / Gut / Akzeptabel)
- Information zu Abholung oder Versand

4.4 Faire Preise und Ehrlichkeit

- Beschreibe den Zustand ehrlich - erwähne Mängel und Gebrauchsspuren
- Setze realistische Preise
- Antworte zügig auf Anfragen
- Halte Zusagen ein

4.5 Sicher Handeln

Tipps für sichere Transaktionen:
- Triff dich an öffentlichen Orten auf dem Campus
- Nimm zur Sicherheit jemanden mit
- Überprüfe den Artikel vor der Bezahlung
- Zahle möglichst bar bei Abholung
- Gib NIEMALS deine Bankdaten oder Passwörter im Chat weiter
- Bei Versand: Nutze sichere Zahlungsmethoden

Achtung: Bei verdächtigen Angeboten (zu guter Preis, Vorkasse ins Ausland,
etc.) brich den Kontakt ab und melde die Anzeige!

4.6 Keine Werbung für externe Dienste

Der Marktplatz ist für private Verkäufe zwischen Studierenden gedacht.
Nicht erlaubt:
- Werbung für externe Shops oder Webseiten
- Multi-Level-Marketing oder Pyramidensysteme
- Dauerhafte gewerbliche Verkäufe (Gewerbe anmelden!)

================================================================================
5. KURSBEWERTUNGEN UND AKADEMISCHE INHALTE
================================================================================

5.1 Kursbewertungen - So geht's richtig

Kursbewertungen helfen anderen Studierenden, gute Kurse zu finden.
Damit sie nützlich bleiben, beachte folgende Regeln:

Erlaubt und erwünscht:
- Deine ehrliche Meinung zur Qualität des Kurses
- Einschätzung zu Schwierigkeit, Arbeitsaufwand und Organisation
- Konstruktive Kritik
- Tipps für andere Studierende
- Lob für gute Lehre

Nicht erlaubt:
- Persönliche Angriffe auf Lehrende (Aussehen, Privatleben, etc.)
- Falsche Tatsachenbehauptungen (z.B. "Prof. X hat plagiiert")
- Koordinierte Absprachen für negative Bewertungen
- Beleidigungen oder Beschimpfungen
- Veröffentlichung privater Informationen über Lehrende

Wichtig: Eine kritische Bewertung ist erlaubt - eine Beleidigung nicht!
Es ist ein Unterschied, ob du schreibst "Die Vorlesung war unstrukturiert
und schwer zu folgen" oder "Prof. X ist inkompetent und hässlich".

5.2 Lehrende können antworten

Wir geben Lehrenden die Möglichkeit, auf Bewertungen zu reagieren.
So entsteht ein fairer Dialog.

5.3 Teilen von Lernmaterialien

Erlaubt:
- Deine eigenen Notizen und Zusammenfassungen
- Von dir erstellte Lernhilfen und Karteikarten
- Links zu frei verfügbaren Ressourcen
- Diskussionen über Kursinhalte

Nicht erlaubt (Urheberrechtsverletzung!):
- Vollständige Lehrbücher oder Buchkapitel
- Vorlesungsfolien ohne Genehmigung des Dozenten
- Unveröffentlichte Forschung
- Prüfungen des aktuellen Semesters
- Lösungen zu bewerteten Aufgaben

Hinweis: Das Hochladen urheberrechtlich geschützter Materialien ist
nach deutschem Recht strafbar (UrhG). Wir müssen solche Inhalte entfernen.

5.4 Akademische Integrität

UNIVERSE ist ein Ort zum Lernen und zum Austausch - aber kein Ort zum
Schummeln. Wir unterstützen keine akademische Unredlichkeit.

Verboten:
- Angebote, Hausarbeiten oder Abschlussarbeiten für andere zu schreiben
- Verkauf von Prüfungslösungen
- Organisierte Betrugsversuche bei Klausuren
- Identitätsbetrug bei Prüfungen

================================================================================
6. NACHRICHTEN UND CHAT
================================================================================

6.1 Respektvolle Kommunikation

Der Chat ist für die Kommunikation rund um den Marktplatz gedacht.
Bitte beachte:

- Sei höflich und respektvoll
- Antworte zeitnah auf Anfragen
- Versende keine unerwünschten Werbebotschaften
- Respektiere es, wenn jemand nicht antworten möchte

6.2 Was im Chat verboten ist

- Belästigung oder Stalking
- Unaufgeforderte sexuelle Nachrichten oder Bilder
- Drohungen oder Einschüchterung
- Spam oder Kettennachrichten
- Betrugsversuche
- Links zu schädlichen Webseiten

6.3 Blockieren und Melden

Wenn dich jemand belästigt:
1. Blockiere die Person - sie kann dir dann keine Nachrichten mehr senden
2. Melde den Chat, wenn ein Verstoß vorliegt
3. Speichere ggf. Beweise (Screenshots)

================================================================================
7. WAS PASSIERT BEI VERSTÖSSEN?
================================================================================

7.1 Unser Stufensystem

Wir glauben an zweite Chancen - aber auch an Konsequenzen. Je nach
Schwere des Verstoßes reagieren wir unterschiedlich:

Stufe 1 - Verwarnung
- Bei leichten oder erstmaligen Verstößen
- Der Inhalt wird entfernt
- Du erhältst eine Benachrichtigung mit Erklärung
- Der Verstoß wird notiert (verfällt nach 6 Monaten)

Stufe 2 - Einschränkung
- Bei wiederholten Verstößen innerhalb von 6 Monaten
- Der Inhalt wird entfernt
- Temporäre Einschränkung der Posting-Funktion (7 Tage)
- Der Verstoß wird notiert (verfällt nach 12 Monaten)

Stufe 3 - Suspendierung
- Bei mehrfachen Verstößen innerhalb von 12 Monaten
- Dein Account wird für 30 Tage gesperrt
- Der Verstoß wird notiert (verfällt nach 24 Monaten)

Stufe 4 - Permanente Sperre
- Bei wiederholten schweren Verstößen
- Oder bei einmaligen besonders schweren Verstößen
- Dein Account wird dauerhaft gesperrt
- Du kannst keinen neuen Account erstellen

7.2 Sofortige Sperrung ohne Vorwarnung

Bei folgenden Verstößen erfolgt eine sofortige permanente Sperre:
- Kindesmissbrauchsdarstellungen (plus Anzeige)
- Konkrete Gewaltdrohungen (plus ggf. Polizeimeldung)
- Verbreitung intimer Aufnahmen ohne Zustimmung
- Betrug im Marktplatz
- Hassrede und Volksverhetzung
- Verwendung verfassungswidriger Symbole

7.3 Entfernte Inhalte

Wenn wir Inhalte entfernen:
- Erhältst du eine Benachrichtigung mit der Begründung
- Wird dir mitgeteilt, welche Regel verletzt wurde
- Erfährst du, welche Konsequenzen das hat
- Wirst du über dein Recht auf Widerspruch informiert

Automatische Entfernung bei Mehrfachmeldungen:
- Wenn 5 oder mehr verschiedene Nutzer deinen Inhalt melden, wird er
  automatisch entfernt (dies dient dem Schutz der Community)
- Du erhältst per E-Mail eine Benachrichtigung mit:
  - Den gemeldeten Kategorien (z.B. Belästigung, Spam, etc.)
  - Der Anzahl der verbleibenden Verstöße bis zur Kontosperrung
  - Hinweisen zum Widerspruchsverfahren
- Du kannst Widerspruch einlegen, und ein Moderator prüft den Fall

Wir speichern entfernte Inhalte für 10 Wochen, um mögliche Widersprüche
prüfen und unseren Dokumentations- und Rechenschaftspflichten nachkommen
zu können (berechtigtes Interesse gemäß Art. 6 Abs. 1 lit. f DSGVO; im
Einklang mit den Vorgaben des Digital Services Act (DSA) zu Beschwerde-
und Rechtsbehelfsverfahren).

================================================================================
8. MELDEN VON VERSTÖSSEN
================================================================================

8.1 So meldest du Inhalte

Wenn du einen Verstoß gegen unsere Richtlinien bemerkst:

1. Tippe auf das Melden-Symbol (Flagge oder drei Punkte) beim Inhalt
2. Wähle die passende Kategorie:
   - Illegaler Inhalt
   - Belästigung/Mobbing
   - Hassrede/Diskriminierung
   - Spam
   - Marktplatz-Verstoß
   - Akademische Integrität
   - Sonstiges
3. Ergänze optional weitere Details
4. Sende die Meldung ab

8.2 Was passiert nach deiner Meldung?

1. Du erhältst eine Bestätigung, dass wir deine Meldung erhalten haben
2. Unser Team prüft den gemeldeten Inhalt
3. Wir treffen eine Entscheidung basierend auf unseren Richtlinien
4. Du wirst informiert, ob Maßnahmen ergriffen wurden

Bearbeitungszeiten:
- Offensichtlich illegale Inhalte: innerhalb von 24 Stunden
- Andere Verstöße: innerhalb von 7 Tagen

8.3 Deine Anonymität ist geschützt

Die gemeldete Person erfährt NICHT, wer die Meldung abgegeben hat.
Wir schützen die Identität von Meldern.

8.4 Falschmeldungen

Bitte melde nur echte Verstöße. Wiederholte Falschmeldungen
(um jemandem zu schaden) können selbst als Verstoß gewertet werden.

================================================================================
9. DEIN RECHT AUF WIDERSPRUCH
================================================================================

9.1 Wenn du nicht einverstanden bist

Wenn wir einen deiner Inhalte entfernt oder deinen Account eingeschränkt
haben und du damit nicht einverstanden bist, hast du das Recht auf
Widerspruch.

Stufe 1 - Interner Widerspruch
- Du hast 14 Tage Zeit nach unserer Entscheidung
- Nutze das Widerspruchsformular in der App
- Erkläre, warum du die Entscheidung für falsch hältst
- Ein anderer Moderator als der ursprüngliche prüft deinen Fall
- Antwort innerhalb von 7 Werktagen

Stufe 2 - Zweitprüfung
- Wenn du mit Stufe 1 nicht einverstanden bist
- Du hast 7 Tage Zeit nach der ersten Entscheidung
- Prüfung durch unser Senior-Team
- Antwort innerhalb von 14 Werktagen
- Finale interne Entscheidung

Stufe 3 - Externe Streitbeilegung
- Du kannst dich an eine zertifizierte Streitbeilegungsstelle wenden
- Informationen dazu erhältst du in der Ablehnungsbegründung
- Für dich kostenfrei (wir tragen die Kosten)

9.2 Wenn dein Widerspruch erfolgreich ist

- Der Inhalt wird innerhalb von 24 Stunden wiederhergestellt
- Der Verstoß wird aus deiner Akte gelöscht
- Du wirst darüber informiert

================================================================================
10. DATENSCHUTZ BEI DER MODERATION
================================================================================

10.1 Wie wir Inhalte prüfen

Wenn ein Inhalt gemeldet wird oder unsere automatischen Systeme
ihn markieren:
- Wird er von geschulten Moderatoren geprüft
- Beachten wir den Kontext des Beitrags
- Treffen wir eine Entscheidung basierend auf diesen Richtlinien
- Dokumentieren wir die Entscheidung

10.2 Automatische Erkennung

Wir nutzen technische Systeme zur Erkennung von:
- Kindesmissbrauchsdarstellungen
- Bekannten terroristischen Inhalten
- Offensichtlichem Spam
- Verdächtigen Marktplatz-Anzeigen
- Unangemessenen Wörtern und Ausdrücken

Diese Systeme unterstützen uns, aber finale Entscheidungen werden
von Menschen getroffen.

10.2a Wortfilter und Inhaltsprüfung

Bevor dein Inhalt veröffentlicht wird, prüft unser System automatisch
auf unangemessene Wörter oder Ausdrücke:

- Wenn unangemessene Wörter erkannt werden, wird der Upload blockiert
- Du erhältst eine Nachricht, die dir zeigt, welche Wörter den Upload
  verhindert haben
- Du kannst deinen Inhalt dann anpassen und erneut versuchen
- Dies dient dem Schutz der Community und hilft dir, unsere Regeln
  einzuhalten

Diese präventive Prüfung ersetzt nicht die manuelle Moderation. Auch
Inhalte ohne blockierte Wörter können gegen unsere Richtlinien verstoßen.

10.3 Speicherung

- Entfernte Inhalte: 10 Wochen (zur Bearbeitung von Widersprüchen; berechtigtes Interesse, Art. 6 Abs. 1 lit. f DSGVO, im Einklang mit dem DSA)
- Meldungen: Bis zur Entscheidung + 90 Tage
- Verstoß-Historie: Je nach Stufe (6-24 Monate)
- Bei erfolgreichem Widerspruch: Sofortige Löschung der Verstoß-Notiz

10.4 Deine Auskunftsrechte

Du kannst jederzeit anfragen:
- Welche Verstöße in deiner Akte notiert sind
- Welche Meldungen du abgegeben hast
- Welche Entscheidungen zu deinen Inhalten getroffen wurden

Details findest du in unserer Datenschutzerklärung.

================================================================================
11. ZUSAMMENARBEIT MIT BEHÖRDEN
================================================================================

11.1 Wann wir Behörden informieren

In folgenden Fällen sind wir gesetzlich verpflichtet, Inhalte an
Behörden zu melden:

- Kindesmissbrauchsdarstellungen -> Bundeskriminalamt (BKA)
- Konkrete Gewaltandrohungen -> Polizei
- Terroristische Inhalte -> Bundeskriminalamt (BKA)

11.2 Anfragen von Behörden

Wir kooperieren mit Strafverfolgungsbehörden bei:
- Gerichtlich angeordneten Anfragen
- Staatsanwaltschaftlichen Ermittlungen
- Polizeilichen Notfällen (Gefahr für Leben)

Soweit rechtlich möglich, informieren wir dich, wenn Behörden
Daten zu deinem Account angefragt haben.

================================================================================
12. ÄNDERUNGEN DIESER RICHTLINIEN
================================================================================

Wir können diese Gemeinschaftsrichtlinien von Zeit zu Zeit aktualisieren,
um auf neue Entwicklungen, rechtliche Anforderungen oder Feedback aus
unserer Community zu reagieren.

Bei wesentlichen Änderungen:
- Informieren wir dich per E-Mail oder In-App-Benachrichtigung
- Geben wir dir Zeit, dich mit den Änderungen vertraut zu machen
- Tritt die neue Version 14 Tage nach Veröffentlichung in Kraft

Die aktuelle Version findest du immer in der App unter
Profil > Community Guideline.

================================================================================
13. KONTAKT
================================================================================

Hast du Fragen zu diesen Richtlinien oder möchtest du einen Verstoß
melden, der nicht über die App-Funktion möglich ist?

E-Mail: community@universe-app.online

================================================================================
14. FAZIT
================================================================================

UNIVERSE lebt von seiner Community - von dir und allen anderen Studierenden,
die hier ihre Erfahrungen teilen, sich gegenseitig helfen und vernetzen.

Mit diesen Richtlinien wollen wir sicherstellen, dass UNIVERSE ein Ort
bleibt, an dem sich alle willkommen und sicher fühlen. Danke, dass du
Teil unserer Community bist und dazu beiträgst, sie positiv zu gestalten!

Dein UNIVERSE Team

================================================================================

Stand: 11. Juli 2026

================================================================================`;

  return (
    <div className="guideline-page">
      {/* Header - Full AppHeader for authenticated users */}
      {isAuthenticated ? (
        <AppHeader
          userData={userData}
          onLogout={handleLogout}
          isLoggingOut={isLoggingOut}
          logoutError={logoutError}
          onClearLogoutError={clearLogoutError}
        />
      ) : (
        <header className="guideline-simple-header">
          <h1 className="guideline-header-title">{t('profile.communityGuidelines')}</h1>
        </header>
      )}

      {/* Tab Navigation - Only for authenticated users */}
      {isAuthenticated && (
        <div className="tab-navigation-guideline">
          <span className="tab-title">{t('profile.communityGuidelines')}</span>
        </div>
      )}

      {/* Community Guideline Content */}
      <main
        className={`guideline-content ${!isAuthenticated ? 'unauthenticated' : ''}`}
        role="main"
        aria-label={t('profile.communityGuidelines')}
      >
        {/* Back Button - Issue #149: Accessibility compliant */}
        <BackButton
          onClick={onNavigateBack}
          className="guideline-back-button"
        />

        {/* Guideline Text - Wrapped in article for semantic structure */}
        <article className="guideline-text" aria-label="Community-Richtlinien Inhalt">
          <pre>{guidelineContent}</pre>
        </article>
      </main>

      {/* Bottom Navigation - Only for authenticated users */}
      {isAuthenticated && (
        <>
          <BottomNavigation
            activeTab="profile"
            onCreateClick={() => setShowCreateOptions(!showCreateOptions)}
          />
          <CreateOptionsMenu
            isOpen={showCreateOptions}
            onClose={() => setShowCreateOptions(false)}
          />
        </>
      )}
    </div>
  );
}

export default CommunityGuidelineContent;
